import { useEffect } from 'react';

/**
 * Closes a dropdown/popover when scroll is detected in any ancestor scroll
 * container — either horizontal or vertical — e.g. when the trigger scrolls
 * out of view behind the sidebar or the sidebar itself is scrolled.
 *
 * @param {boolean} isOpen - Whether the dropdown is currently open.
 * @param {React.RefObject} triggerRef - Ref attached to the trigger element.
 * @param {() => void} onClose - Callback to close the dropdown.
 * @param {number} threshold - Scroll distance (px) before closing. Default 40.
 */
const useCloseOnScroll = (isOpen, triggerRef, onClose, threshold = 40) => {
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const scrollables = [];
    let el = triggerRef.current.parentElement;
    while (el) {
      const { overflow, overflowX, overflowY } = window.getComputedStyle(el);
      if (/auto|scroll/.test(overflow + overflowX + overflowY)) {
        scrollables.push({
          el,
          initialScrollLeft: el.scrollLeft,
          initialScrollTop: el.scrollTop,
        });
      }
      el = el.parentElement;
    }

    const handlers = scrollables.map((entry) => {
      const handler = () => {
        const hDelta = Math.abs(entry.el.scrollLeft - entry.initialScrollLeft);
        const vDelta = Math.abs(entry.el.scrollTop - entry.initialScrollTop);
        if (hDelta > threshold || vDelta > threshold) {
          onClose();
        }
      };
      entry.el.addEventListener('scroll', handler);
      return { el: entry.el, handler };
    });

    return () => handlers.forEach(({ el, handler }) => el.removeEventListener('scroll', handler));
  }, [isOpen, triggerRef, onClose, threshold]);
};

export default useCloseOnScroll;
